import { computed, ref } from 'vue'

import type { ComputedRef, WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { SortTasksKanbanBy, SortTasksKanbanDirection } from '../enums'

interface FilterSortOptions {
  initialSortBy: string | null
  initialSortDirection: string | null
}

interface FilterSortReturn {
  options: AppSelectOption[]
  model: WritableComputedRef<string | undefined>
  sortBy: ComputedRef<string | null>
  sortDirection: ComputedRef<string | null>
}

interface SortListItem {
  value: string
  text: string
  sortBy: string
  sortDirection: string
}

const sortList: SortListItem[] = [
  {
    value: `${SortTasksKanbanBy.UpdatedAt}:${SortTasksKanbanDirection.ASC}`,
    text: 'By update',
    sortBy: SortTasksKanbanBy.UpdatedAt,
    sortDirection: SortTasksKanbanDirection.DESC,
  },
  {
    value: `${SortTasksKanbanBy.CreatedAt}:${SortTasksKanbanDirection.ASC}`,
    text: 'By created',
    sortBy: SortTasksKanbanBy.CreatedAt,
    sortDirection: SortTasksKanbanDirection.DESC,
  },
  {
    value: `${SortTasksKanbanBy.Deadline}:${SortTasksKanbanDirection.ASC}`,
    text: 'By deadline',
    sortBy: SortTasksKanbanBy.Deadline,
    sortDirection: SortTasksKanbanDirection.ASC,
  },
]

const buildSortValue = (sortBy: string | null, sortDirection: string | null): string | null => {
  if (!sortBy) return null

  return sortList.find(item => item.sortBy === sortBy && item.sortDirection === sortDirection)?.value
    ?? sortList.find(item => item.sortBy === sortBy)?.value
    ?? null
}

export const useFilterSort = ({ initialSortBy, initialSortDirection }: FilterSortOptions): FilterSortReturn => {
  const query = ref<string | null>(buildSortValue(initialSortBy, initialSortDirection))

  const options: AppSelectOption[] = sortList.map(item => ({
    value: item.value,
    text: item.text,
  }))

  const model = computed<string | undefined>({
    get: () => query.value ?? undefined,
    set: (value) => { query.value = value ?? null },
  })

  const sortBy = computed<string | null>(() => {
    if (!query.value) return null
    return sortList.find(item => item.value === query.value)?.sortBy ?? null
  })

  const sortDirection = computed<string | null>(() => {
    if (!query.value) return null
    return sortList.find(item => item.value === query.value)?.sortDirection ?? null
  })

  return {
    options,
    model,
    sortBy,
    sortDirection,
  }
}
